import {
    FileUrls,
    GetOtpTokenResponse,
    TrackListenedHistory,
    UserList,
    UserPurchasesResponse,
    FileUrl,
} from "../types/types";
import { fetchAPI } from "./utils";

export type UsageDeclineMode = "7d" | "30d" | "yoy" | "custom";
export type UsageDeclineCategory = "overview" | "appHub" | "studio";

export interface UsageDeclineMetric {
    current: number;
    previous: number;
    delta: number;
    deltaPct: number | null;
}

export interface UsageDeclineAppHubChannel {
    trackStarts: UsageDeclineMetric;
    hubUniques: UsageDeclineMetric;
    combinedCurrent: number;
    combinedPrevious: number;
    combinedDelta: number;
    combinedDeltaPct: number | null;
    isIssue: boolean;
    wentDark: boolean;
    isGrowing: boolean;
    lowBaseline: boolean;
    priorityScore: number;
}

export interface UsageDeclineStudioChannel {
    jobs: UsageDeclineMetric;
    activeAdmins: UsageDeclineMetric;
    chars: UsageDeclineMetric;
    combinedCurrent: number;
    combinedPrevious: number;
    combinedDelta: number;
    combinedDeltaPct: number | null;
    isIssue: boolean;
    wentDark: boolean;
    isGrowing: boolean;
    lowBaseline: boolean;
    priorityScore: number;
}

export interface UsageDeclineOverviewCompany {
    companyId: string;
    name: string;
    appHub: UsageDeclineAppHubChannel;
    studio: UsageDeclineStudioChannel;
    priorityScore: number;
    bothDeclining: boolean;
    wentDark: boolean;
    isIssue: boolean;
    lowBaseline: boolean;
    isGrowing: boolean;
    combinedCurrent: number;
    combinedPrevious: number;
    combinedDelta: number;
    lastActivityAt: string | null;
}

export interface UsageDeclineAppHubCompany {
    companyId: string;
    name: string;
    trackStarts: UsageDeclineMetric;
    hubUniques: UsageDeclineMetric;
    combinedCurrent: number;
    combinedPrevious: number;
    combinedDelta: number;
    combinedDeltaPct: number | null;
    lowBaseline: boolean;
    isIssue: boolean;
    wentDark: boolean;
    isGrowing: boolean;
    priorityScore: number;
    lastActivityAt: string | null;
}

export interface UsageDeclineStudioCompany {
    companyId: string;
    name: string;
    jobs: UsageDeclineMetric;
    activeAdmins: UsageDeclineMetric;
    chars: UsageDeclineMetric;
    combinedCurrent: number;
    combinedPrevious: number;
    combinedDelta: number;
    combinedDeltaPct: number | null;
    lowBaseline: boolean;
    isIssue: boolean;
    wentDark: boolean;
    isGrowing: boolean;
    priorityScore: number;
    lastActivityAt: string | null;
}

export interface UsageDeclineResponse {
    period: {
        mode: UsageDeclineMode;
        current: { from: string; to: string };
        previous: { from: string; to: string };
    };
    category: UsageDeclineCategory;
    companies: Array<UsageDeclineOverviewCompany | UsageDeclineAppHubCompany | UsageDeclineStudioCompany>;
    summary: {
        needSupportCount: number;
        wentDarkCount: number;
        bothDecliningCount: number;
        growingCount: number;
        topActive: { companyId: string; name: string; volume: number } | null;
    };
}

export interface GetUsageDeclineParams {
    token: string;
    mode: UsageDeclineMode;
    category: UsageDeclineCategory;
    includeLowBaseline?: boolean;
    currentFrom?: string;
    currentTo?: string;
    previousFrom?: string;
    previousTo?: string;
}

export const getUsageDecline = async (params: GetUsageDeclineParams): Promise<UsageDeclineResponse> => {
    const { token, ...body } = params;
    return fetchAPI<UsageDeclineResponse>("/get-usage-decline", "POST", body, token);
};

export const getTrackListenedStoriesByDate = async (
    token: string,
    trackId: string,
    startDate: Date,
    endDate: Date,
): Promise<TrackListenedHistory> => {
    return fetchAPI<TrackListenedHistory>(
        "/get-track-listened-stories-by-date",
        "POST",
        { trackId, startDate, endDate },
        token,
    );
};

export const getUserInfo = async (token: string, userId: string): Promise<UserPurchasesResponse> => {
    return fetchAPI<UserPurchasesResponse>("/get-user-info", "POST", { userId }, token);
};

export const getUserList = async (token: string, startDate: Date, endDate: Date): Promise<UserList> => {
    return fetchAPI<UserList>("/get-user-list", "POST", { startDate, endDate }, token);
};

export const generateTokensBusinessCatalog = async (
    token: string,
    businessCatalogId: string,
    numberTokens: number,
    isUnlimited: boolean,
): Promise<FileUrls> => {
    return fetchAPI<FileUrls>(
        "/generate-tokens-business-catalog",
        "POST",
        { businessCatalogId, numberTokens, isUnlimited },
        token,
    );
};

export const generateQRCodeFromText = async (token: string, text: string): Promise<FileUrl> => {
    return fetchAPI<FileUrl>("/generate-qr-code-from-text", "POST", { text }, token);
};

export const getPurchaseInfo = async (token: string, reservationId: string): Promise<GetOtpTokenResponse> => {
    return fetchAPI<GetOtpTokenResponse>("/get-reservation-details", "POST", { reservationId }, token);
};
